import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { useAuth, useUser } from '@clerk/clerk-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import * as THREE from 'three'

gsap.registerPlugin(ScrollTrigger)

const DEFAULT_POSTS = [
  {
    id: 1,
    title: 'Morning with the Blue Jays',
    excerpt: 'An incredible encounter with a family of blue jays. Their vibrant plumage and playful calls made for an unforgettable dawn.',
    content: `An incredible encounter with a family of blue jays greeted me this morning just before sunrise. I stepped out onto the porch with my coffee, and there they were — five blue jays perched along the old oak at the edge of the yard, their cobalt feathers catching the first pale light of dawn.

Blue jays are often underestimated. People dismiss them as loud and aggressive, but watching this family interact told a different story. The adults were clearly teaching the juveniles where to find the best acorns, demonstrating the cache-and-retrieve behaviour that makes blue jays one of the most intelligent birds in North America.

Their calls shifted constantly — the sharp alarm cry when a neighbour's cat appeared, a soft melodic warble between the pair, and the remarkable mimicry of a red-shouldered hawk that sent every other bird scattering from the feeder. That mimicry is deliberate. Scientists believe blue jays use hawk calls to clear feeding areas for themselves.

By 7 a.m. they had moved on, likely following the acorn mast deeper into the woods. But those two quiet hours reminded me why I started bird watching in the first place — not to tick species off a list, but to sit still long enough for the world to reveal itself.`,
    author: 'Dr. Sarah Wilson',
    userImage: null,
    date: '2024-01-15',
    image: 'https://images.pexels.com/photos/1636064/pexels-photo-1636064.jpeg?auto=compress&cs=tinysrgb&w=800',
    isUserPost: false,
    likes: 45,
  },
  {
    id: 2,
    title: 'Eagle Spotting at the Lake',
    excerpt: 'Witnessed a bald eagle catching fish at sunrise. The misty lake and powerful dive left me completely speechless.',
    content: `The alarm went off at 4:45 a.m. and honestly, for a moment I almost silenced it. But the forecast had promised still air and low mist over the lake — perfect conditions — so I dragged myself out into the cold dark and drove the forty minutes to the reservoir.

I was set up on the eastern bank by first light, tripod sunk into soft mud, when I saw the silhouette. A bald eagle, motionless on a dead snag thirty metres above the water, surveying the glassy surface with the patience of something that has no concept of being late.

For twenty minutes nothing happened. The mist rolled. A great blue heron stalked the shallows at the far shore. Then the eagle dropped.

It wasn't the explosion of power I expected. It was surgical — a controlled tuck, wings half-folded, feet forward, and then the impact. A geyser of white water, a massive wingbeat, and it was climbing again with a large rainbow trout writhing in its talons. The whole dive took under three seconds.

I've replayed the footage dozens of times. What strikes me most isn't the speed or the strength — it's the precision. Eagles can see ultraviolet light, which makes fish just below the surface glow. Every dive is calculated from information we literally cannot perceive. That morning I felt genuinely small, and genuinely grateful for it.`,
    author: 'John Davis',
    userImage: null,
    date: '2024-01-10',
    image: 'https://images.pexels.com/photos/2629372/pexels-photo-2629372.jpeg?auto=compress&cs=tinysrgb&w=800',
    isUserPost: false,
    likes: 32,
  },
  {
    id: 3,
    title: 'Hummingbird Migration Magic',
    excerpt: 'Ruby-throated hummingbirds paused in our garden during fall migration — dozens of tiny jewels buzzing with pure energy.',
    content: `Every September our garden transforms. The salvias come into their final flush, the cardinal flowers glow scarlet along the fence, and for about ten days — if we're lucky — the ruby-throated hummingbirds descend.

This year we counted thirty-two individuals in a single afternoon. Thirty-two. At feeders, at flowers, chasing each other in tight spirals above the bee balm, their iridescent gorgets flashing green and then suddenly, impossibly, ruby-red as the angle of light shifts. The sound is constant — that high-pitched chip note, the mechanical whir of wings beating 53 times per second.

What staggers me every year is the journey they're preparing for. These birds, each weighing less than a nickel, are about to fly non-stop across the Gulf of Mexico. Roughly 800 kilometres of open water. No rest, no food, no land. They double their body weight in fat before departing, and they burn through all of it over those eighteen to twenty-two hours of continuous flight.

I watched one tiny female perch on a wire for a full four minutes — an eternity for a hummingbird — and I found myself thinking about what it must feel like to carry that kind of imperative inside you. To have your entire biology oriented toward a place a thousand kilometres away that you have never been but somehow already know.

She flew south just before dark. I watched until she disappeared into the tree line. I'll watch for her again next April.`,
    author: 'Emily Chen',
    userImage: null,
    date: '2024-01-05',
    image: 'https://images.pexels.com/photos/349758/hummingbird-bird-birds-349758.jpeg?auto=compress&cs=tinysrgb&w=800',
    isUserPost: false,
    likes: 67,
  },
]

const getReadingTime = (content) => {
  const words = content?.trim().split(/\s+/).length || 0
  const minutes = Math.ceil(words / 200)
  return minutes <= 1 ? '1 min read' : `${minutes} min read`
}

const EditModal = ({ post, onSave, onClose }) => {
  const [title, setTitle] = useState(post.title)
  const [content, setContent] = useState(post.content)
  const [visibility, setVisibility] = useState(post.visibility || 'public')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!title.trim() || !content.trim()) { toast.error('Please fill in all fields'); return }
    onSave({ ...post, title: title.trim(), content: content.trim(), visibility, excerpt: content.trim().substring(0, 150) + '...' })
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">Edit Story</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Title</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)} required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition placeholder-slate-400" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Content</label>
              <textarea value={content} onChange={e => setContent(e.target.value)} rows={8} required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition resize-none placeholder-slate-400" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">Visibility</label>
              <select value={visibility} onChange={e => setVisibility(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 text-white rounded-xl focus:ring-2 focus:ring-emerald-500 transition">
                <option value="public">🌍 Public — Everyone can see</option>
                <option value="private">🔒 Private — Only you can see</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600 text-white py-3 rounded-xl font-semibold hover:from-emerald-500 hover:to-green-500 transition">Save Changes</button>
              <button type="button" onClick={onClose} className="flex-1 bg-slate-700 text-slate-300 py-3 rounded-xl font-semibold hover:bg-slate-600 transition">Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

const BlogDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)
  const [liked, setLiked] = useState(false)
  const [editingPost, setEditingPost] = useState(null)
  const [comments, setComments] = useState([])
  const [commentText, setCommentText] = useState('')
  const [isSubmittingComment, setIsSubmittingComment] = useState(false)
  const [readProgress, setReadProgress] = useState(0)
  const [showScrollTop, setShowScrollTop] = useState(false)

  const { isSignedIn, userId } = useAuth()
  const { user } = useUser()

  const likeInProgressRef = useRef(false)
  const hasLikedRef = useRef(false)

  // Animation refs
  const threeCanvasRef = useRef(null)
  const heroImageRef = useRef(null)
  const heroBadgeRef = useRef(null)
  const heroTitleRef = useRef(null)
  const backBtnRef = useRef(null)
  const authorCardRef = useRef(null)
  const articleBodyRef = useRef(null)
  const paragraphRefs = useRef([])
  const commentsRef = useRef(null)
  const progressBarRef = useRef(null)

  // ─── Three.js: calm ambient background ─────────────────────────────
  useEffect(() => {
    const canvas = threeCanvasRef.current
    if (!canvas) return

    const scene = new THREE.Scene()
    const w = window.innerWidth
    const h = window.innerHeight
    const camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 100)
    camera.position.z = 8

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false })
    renderer.setSize(w, h)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setClearColor(0x000000, 0)

    // Same emerald palette, calmer for reading
    const palette = [
      [0.07, 0.74, 0.40],
      [0.04, 0.60, 0.48],
      [0.30, 0.90, 0.56],
      [0.50, 0.96, 0.66],
      [0.10, 0.48, 0.28],
    ]

    // Layer 1: small drifting particles
    const COUNT = 180
    const pos = new Float32Array(COUNT * 3)
    const col = new Float32Array(COUNT * 3)
    for (let i = 0; i < COUNT; i++) {
      pos[i * 3]     = (Math.random() - 0.5) * 20
      pos[i * 3 + 1] = (Math.random() - 0.5) * 12
      pos[i * 3 + 2] = (Math.random() - 0.5) * 6
      const c = palette[Math.floor(Math.random() * palette.length)]
      col[i * 3] = c[0]; col[i * 3 + 1] = c[1]; col[i * 3 + 2] = c[2]
    }
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3))
    const mat = new THREE.PointsMaterial({
      size: 0.055, vertexColors: true, transparent: true,
      opacity: 0.65, blending: THREE.AdditiveBlending, depthWrite: false,
    })
    const points = new THREE.Points(geo, mat)
    scene.add(points)

    // Layer 2: large slow bokeh orbs
    const BIG = 35
    const bpos = new Float32Array(BIG * 3)
    const bcol = new Float32Array(BIG * 3)
    for (let i = 0; i < BIG; i++) {
      bpos[i * 3]     = (Math.random() - 0.5) * 18
      bpos[i * 3 + 1] = (Math.random() - 0.5) * 10
      bpos[i * 3 + 2] = (Math.random() - 0.5) * 3
      const c = palette[Math.floor(Math.random() * palette.length)]
      bcol[i * 3] = c[0] * 0.5; bcol[i * 3 + 1] = c[1] * 0.5; bcol[i * 3 + 2] = c[2] * 0.5
    }
    const bgeo = new THREE.BufferGeometry()
    bgeo.setAttribute('position', new THREE.BufferAttribute(bpos, 3))
    bgeo.setAttribute('color', new THREE.BufferAttribute(bcol, 3))
    const bmat = new THREE.PointsMaterial({
      size: 0.32, vertexColors: true, transparent: true,
      opacity: 0.28, blending: THREE.AdditiveBlending, depthWrite: false,
    })
    const bigPoints = new THREE.Points(bgeo, bmat)
    scene.add(bigPoints)

    // Layer 3: medium connection-point particles (no lines, just bigger)
    const MED = 60
    const mpos = new Float32Array(MED * 3)
    const mcol = new Float32Array(MED * 3)
    for (let i = 0; i < MED; i++) {
      mpos[i * 3]     = (Math.random() - 0.5) * 18
      mpos[i * 3 + 1] = (Math.random() - 0.5) * 10
      mpos[i * 3 + 2] = (Math.random() - 0.5) * 4
      const c = palette[Math.floor(Math.random() * palette.length)]
      mcol[i * 3] = c[0] * 0.8; mcol[i * 3 + 1] = c[1] * 0.8; mcol[i * 3 + 2] = c[2] * 0.8
    }
    const mgeo = new THREE.BufferGeometry()
    mgeo.setAttribute('position', new THREE.BufferAttribute(mpos, 3))
    mgeo.setAttribute('color', new THREE.BufferAttribute(mcol, 3))
    const mmat = new THREE.PointsMaterial({
      size: 0.14, vertexColors: true, transparent: true,
      opacity: 0.45, blending: THREE.AdditiveBlending, depthWrite: false,
    })
    const medPoints = new THREE.Points(mgeo, mmat)
    scene.add(medPoints)

    // Velocities (slower than blog list for reading atmosphere)
    const velocities = Array.from({ length: COUNT }, () => ({
      x: (Math.random() - 0.5) * 0.003,
      y: (Math.random() - 0.5) * 0.002,
      phase: Math.random() * Math.PI * 2,
    }))

    const mouse = { x: 0, y: 0 }
    const onMouseMove = (e) => {
      mouse.x = (e.clientX / window.innerWidth - 0.5) * 2
      mouse.y = -(e.clientY / window.innerHeight - 0.5) * 2
    }
    window.addEventListener('mousemove', onMouseMove)

    const onResize = () => {
      const nw = window.innerWidth, nh = window.innerHeight
      camera.aspect = nw / nh
      camera.updateProjectionMatrix()
      renderer.setSize(nw, nh)
    }
    window.addEventListener('resize', onResize)

    let rafId, t = 0
    const animate = () => {
      rafId = requestAnimationFrame(animate)
      t += 0.005 // slower tick for calm reading feel

      const p = geo.attributes.position.array
      for (let i = 0; i < COUNT; i++) {
        p[i * 3]     += velocities[i].x
        p[i * 3 + 1] += velocities[i].y + Math.sin(t + velocities[i].phase) * 0.0015
        if (p[i * 3] >  10) p[i * 3] = -10
        else if (p[i * 3] < -10) p[i * 3] = 10
        if (p[i * 3 + 1] >  6) p[i * 3 + 1] = -6
        else if (p[i * 3 + 1] < -6) p[i * 3 + 1] = 6
      }
      geo.attributes.position.needsUpdate = true

      // Gentle rotation
      bigPoints.rotation.z = t * 0.006
      medPoints.rotation.z = -t * 0.004

      // Mouse parallax (subtle for reading page)
      points.rotation.x    += (mouse.y * 0.025 - points.rotation.x) * 0.03
      points.rotation.y    += (mouse.x * 0.025 - points.rotation.y) * 0.03
      bigPoints.rotation.x += (mouse.y * 0.015 - bigPoints.rotation.x) * 0.02
      bigPoints.rotation.y += (mouse.x * 0.015 - bigPoints.rotation.y) * 0.02

      renderer.render(scene, camera)
    }
    animate()

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('resize', onResize)
      geo.dispose(); mat.dispose()
      bgeo.dispose(); bmat.dispose()
      mgeo.dispose(); mmat.dispose()
      renderer.dispose()
    }
  }, [])

  // ─── Load post ──────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const userBlogs = JSON.parse(localStorage.getItem('userBlogs') || '[]')
      const found = [...userBlogs, ...DEFAULT_POSTS].find(p => p.id === parseInt(id))
      if (!found) { toast.error('Post not found'); navigate('/blog'); return }
      setPost(found)
    } catch { toast.error('Failed to load post'); navigate('/blog') }
    finally { setLoading(false) }
  }, [id, navigate])

  // ─── GSAP: hero entrance timeline ──────────────────────────────────
  useEffect(() => {
    if (loading || !post) return
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

      // Ken Burns: hero image zooms out from slightly enlarged
      if (heroImageRef.current) {
        tl.fromTo(heroImageRef.current,
          { scale: 1.12, filter: 'brightness(0.6)' },
          { scale: 1, filter: 'brightness(1)', duration: 1.6, ease: 'power2.out' },
          0
        )
      }
      // Back button slides in from left
      if (backBtnRef.current) {
        tl.fromTo(backBtnRef.current,
          { x: -40, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.5, ease: 'power2.out' },
          0.2
        )
      }
      // Community badge
      if (heroBadgeRef.current) {
        tl.fromTo(heroBadgeRef.current,
          { y: 16, opacity: 0, scale: 0.85 },
          { y: 0, opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(1.8)' },
          0.5
        )
      }
      // Title reveal (clip-path wipe up)
      if (heroTitleRef.current) {
        tl.fromTo(heroTitleRef.current,
          { y: 50, opacity: 0, skewY: 3 },
          { y: 0, opacity: 1, skewY: 0, duration: 0.75 },
          0.55
        )
      }
      // Author card slides up
      if (authorCardRef.current) {
        tl.fromTo(authorCardRef.current,
          { y: 35, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out' },
          0.75
        )
      }
      // Article body
      if (articleBodyRef.current) {
        tl.fromTo(articleBodyRef.current,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.55, ease: 'power2.out' },
          0.9
        )
      }
    })
    return () => ctx.revert()
  }, [loading, post])

  // ─── GSAP: paragraph scroll-reveal ─────────────────────────────────
  useEffect(() => {
    if (loading || !post) return
    const ctx = gsap.context(() => {
      paragraphRefs.current.forEach((el) => {
        if (!el) return
        gsap.fromTo(el,
          { opacity: 0, y: 22, filter: 'blur(4px)' },
          {
            opacity: 1, y: 0, filter: 'blur(0px)',
            duration: 0.55, ease: 'power2.out',
            scrollTrigger: {
              trigger: el,
              start: 'top 92%',
              toggleActions: 'play none none reverse',
            },
          }
        )
      })
      // Comments section
      if (commentsRef.current) {
        gsap.fromTo(commentsRef.current,
          { opacity: 0, y: 40 },
          {
            opacity: 1, y: 0, duration: 0.65, ease: 'power2.out',
            scrollTrigger: { trigger: commentsRef.current, start: 'top 88%' },
          }
        )
      }
    })
    return () => ctx.revert()
  }, [loading, post])

  // ─── Reading progress bar ───────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      const el = articleBodyRef.current
      if (!el) return
      const { top, height } = el.getBoundingClientRect()
      const vh = window.innerHeight
      const progress = Math.min(100, Math.max(0, ((vh - top) / (height + vh)) * 100))
      setReadProgress(progress)
      setShowScrollTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // ─── Auth / like / comment state ────────────────────────────────────
  useEffect(() => {
    if (userId && post) {
      const stored = JSON.parse(localStorage.getItem(`likes_${userId}`) || '[]')
      const already = stored.includes(post.id)
      setLiked(already)
      hasLikedRef.current = already
    }
  }, [userId, post])

  useEffect(() => {
    if (post) {
      try { setComments(JSON.parse(localStorage.getItem(`comments_${post.id}`) || '[]')) }
      catch { setComments([]) }
    }
  }, [post])

  // ─── Handlers ───────────────────────────────────────────────────────
  const handleLike = useCallback(() => {
    if (!isSignedIn || !userId) { toast.error('Please sign in to like posts'); return }
    if (hasLikedRef.current || likeInProgressRef.current) { toast('Already liked!', { icon: '❤️' }); return }
    likeInProgressRef.current = true
    hasLikedRef.current = true

    const stored = JSON.parse(localStorage.getItem(`likes_${userId}`) || '[]')
    if (!stored.includes(post.id)) {
      stored.push(post.id)
      localStorage.setItem(`likes_${userId}`, JSON.stringify(stored))
    }
    setLiked(true)
    setPost(prev => {
      const updated = { ...prev, likes: (prev.likes || 0) + 1 }
      if (prev.isUserPost) {
        const ub = JSON.parse(localStorage.getItem('userBlogs') || '[]')
        localStorage.setItem('userBlogs', JSON.stringify(ub.map(p => p.id === post.id ? { ...p, likes: updated.likes } : p)))
      }
      return updated
    })

    // Heart burst animation
    gsap.timeline()
      .to('.like-button', { scale: 1.4, duration: 0.12, ease: 'power2.out' })
      .to('.like-button', { scale: 0.9, duration: 0.1 })
      .to('.like-button', { scale: 1, duration: 0.2, ease: 'elastic.out(1, 0.5)' })

    toast.success('Liked!')
    likeInProgressRef.current = false
  }, [isSignedIn, userId, post])

  const handleDelete = useCallback(() => {
    if (!isSignedIn || !userId) { toast.error('Please sign in'); return }
    if (post.userId !== userId) { toast.error('You can only delete your own posts'); return }
    if (!window.confirm('Delete this story?')) return
    try {
      const ub = JSON.parse(localStorage.getItem('userBlogs') || '[]')
      localStorage.setItem('userBlogs', JSON.stringify(ub.filter(p => p.id !== post.id)))
      localStorage.removeItem(`comments_${post.id}`)
      toast.success('Story deleted')
      navigate('/blog')
    } catch { toast.error('Failed to delete post') }
  }, [isSignedIn, userId, post, navigate])

  const handleEdit = useCallback(() => {
    if (!isSignedIn || post.userId !== userId) { toast.error('You can only edit your own posts'); return }
    setEditingPost(post)
  }, [isSignedIn, userId, post])

  const handleSaveEdit = useCallback((updatedPost) => {
    const ub = JSON.parse(localStorage.getItem('userBlogs') || '[]')
    localStorage.setItem('userBlogs', JSON.stringify(ub.map(p => p.id === updatedPost.id ? updatedPost : p)))
    setPost(updatedPost)
    setEditingPost(null)
    toast.success('Story updated!')
  }, [])

  const handleAddComment = useCallback(() => {
    if (!isSignedIn || !userId) { toast.error('Please sign in to comment'); return }
    const text = commentText.trim()
    if (!text) return
    setIsSubmittingComment(true)

    const isDuplicate = comments.some(c =>
      c.userId === userId && c.content === text && Date.now() - new Date(c.date).getTime() < 30000
    )
    if (isDuplicate) { toast.error('You just posted that comment!'); setIsSubmittingComment(false); return }

    const newComment = {
      id: Date.now(), userId,
      author: user?.fullName || user?.emailAddresses?.[0]?.emailAddress || 'Anonymous',
      userImage: user?.imageUrl || null,
      content: text,
      date: new Date().toISOString(),
    }
    const updated = [...comments, newComment]
    setComments(updated)
    localStorage.setItem(`comments_${post.id}`, JSON.stringify(updated))
    setCommentText('')
    toast.success('Comment posted!')
    setIsSubmittingComment(false)

    // Animate new comment in
    setTimeout(() => {
      const newEl = document.querySelector(`[data-comment-id="${newComment.id}"]`)
      if (newEl) gsap.fromTo(newEl, { opacity: 0, y: 20, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 0.4, ease: 'power2.out' })
    }, 50)
  }, [isSignedIn, userId, user, commentText, comments, post])

  const handleDeleteComment = useCallback((commentId) => {
    const comment = comments.find(c => c.id === commentId)
    if (!comment || comment.userId !== userId) { toast.error('You can only delete your own comments'); return }
    const el = document.querySelector(`[data-comment-id="${commentId}"]`)
    const doDelete = () => {
      const updated = comments.filter(c => c.id !== commentId)
      setComments(updated)
      localStorage.setItem(`comments_${post.id}`, JSON.stringify(updated))
    }
    if (el) {
      gsap.to(el, { opacity: 0, x: -20, height: 0, marginBottom: 0, duration: 0.3, ease: 'power2.in', onComplete: doDelete })
    } else { doDelete() }
    toast.success('Comment deleted')
  }, [comments, userId, post])

  // ─── Loading ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-emerald-500 absolute" />
            <div className="text-2xl absolute inset-0 flex items-center justify-center">🐦</div>
          </div>
          <p className="text-emerald-400 font-medium">Loading story...</p>
        </div>
      </div>
    )
  }

  if (!post) return null

  const isOwner = post.isUserPost && post.userId === userId
  const paragraphs = post.content.split('\n').filter(p => p.trim())

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-x-hidden">

      {/* ── Three.js canvas ──────────────────────────────────────── */}
      <canvas
        ref={threeCanvasRef}
        className="fixed inset-0 w-full h-full pointer-events-none"
        style={{ zIndex: 0, opacity: 0.45 }}
      />

      {/* ── Radial depth overlay ─────────────────────────────────── */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 1, background: 'radial-gradient(ellipse 70% 50% at 50% 10%, rgba(16,185,129,0.06) 0%, transparent 70%)' }} />

      {/* ── Reading progress bar ─────────────────────────────────── */}
      <div className="fixed top-0 left-0 right-0 h-0.5 z-50 bg-slate-800/60">
        <div
          ref={progressBarRef}
          className="h-full bg-gradient-to-r from-emerald-500 via-green-400 to-teal-400 transition-none origin-left"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      {/* ── Scroll-to-top button ─────────────────────────────────── */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-6 z-40 w-11 h-11 bg-emerald-500/90 hover:bg-emerald-400 text-white rounded-full shadow-lg shadow-emerald-500/30 flex items-center justify-center transition-all hover:scale-110 backdrop-blur-sm"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}

      {/* ── Page content ─────────────────────────────────────────── */}
      <div className="relative" style={{ zIndex: 2 }}>

        {/* ═══ HERO ══════════════════════════════════════════════════ */}
        <div className="relative w-full h-80 md:h-[28rem] overflow-hidden">
          {post.image ? (
            <img
              ref={heroImageRef}
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover"
              onError={e => { e.target.src = 'https://via.placeholder.com/1400x600?text=Bird+Story' }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-emerald-900 to-slate-900 flex items-center justify-center text-9xl">
              🐦
            </div>
          )}

          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-slate-900/20" />
          {/* Left vignette */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/40 to-transparent" />

          {/* Back button */}
          <button
            ref={backBtnRef}
            onClick={() => navigate('/blog')}
            className="absolute top-5 left-5 flex items-center gap-2 bg-slate-900/50 backdrop-blur-md text-white border border-white/10 px-4 py-2 rounded-full hover:bg-slate-800/70 hover:border-emerald-500/30 transition-all font-medium text-sm group"
          >
            <svg className="w-4 h-4 transform group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to stories
          </button>

          {/* Progress indicator in top-right */}
          <div className="absolute top-5 right-5 flex items-center gap-2 bg-slate-900/50 backdrop-blur-md border border-white/10 px-3 py-1.5 rounded-full">
            <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <span className="text-xs text-slate-300 font-medium">{getReadingTime(post.content)}</span>
          </div>

          {/* Hero text overlay */}
          <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 pb-8 pt-16">
            <div className="max-w-4xl mx-auto">
              {post.isUserPost && (
                <div ref={heroBadgeRef} className="mb-3">
                  <span className="inline-flex items-center gap-1.5 bg-blue-500/70 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full">
                    Community Story
                  </span>
                </div>
              )}
              <h1
                ref={heroTitleRef}
                className="text-2xl sm:text-3xl md:text-5xl font-extrabold text-white leading-tight"
                style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
              >
                {post.title}
              </h1>
            </div>
          </div>
        </div>

        {/* ═══ ARTICLE CONTENT ═══════════════════════════════════════ */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-4 pb-20 relative">

          {/* Author / meta card */}
          <div
            ref={authorCardRef}
            className="bg-slate-800/80 backdrop-blur-sm border border-slate-700/60 rounded-2xl p-5 mb-6 shadow-2xl"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {post.userImage ? (
                  <img src={post.userImage} alt="" className="w-12 h-12 rounded-full ring-2 ring-emerald-500/30" />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center text-lg font-bold text-white">
                    {post.author?.charAt(0) || 'U'}
                  </div>
                )}
                <div>
                  <p className="font-bold text-white">{post.author || 'Anonymous'}</p>
                  <div className="flex items-center gap-2 text-sm text-slate-400 flex-wrap">
                    <span>{format(new Date(post.date), 'MMMM dd, yyyy')}</span>
                    <span className="text-slate-600">·</span>
                    <span className="text-emerald-400">{getReadingTime(post.content)}</span>
                    {post.isUserPost && post.visibility === 'private' && (
                      <><span className="text-slate-600">·</span><span className="text-slate-500">🔒 Private</span></>
                    )}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleLike}
                  disabled={liked}
                  className={`like-button flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                    liked
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30 cursor-default'
                      : 'bg-slate-700/60 text-slate-300 border border-slate-600/50 hover:bg-red-500/15 hover:text-red-400 hover:border-red-500/30'
                  }`}
                >
                  <span className="text-base">{liked ? '❤️' : '🤍'}</span>
                  <span>{post.likes || 0}</span>
                  <span>{liked ? 'Liked' : 'Like'}</span>
                </button>

                {isOwner && (
                  <>
                    <button
                      onClick={handleEdit}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30 hover:bg-blue-500/25 font-semibold text-sm transition"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={handleDelete}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 font-semibold text-sm transition"
                    >
                      🗑️ Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Article body */}
          <div
            ref={articleBodyRef}
            className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 md:p-10 mb-6 shadow-xl"
          >
            {/* Decorative quote mark */}
            <div className="text-7xl font-serif text-emerald-500/15 leading-none mb-2 select-none">"</div>

            {paragraphs.map((para, i) => (
              <p
                key={i}
                ref={el => paragraphRefs.current[i] = el}
                className={`leading-8 mb-6 last:mb-0 ${
                  i === 0
                    ? 'text-xl text-slate-200 font-medium'
                    : 'text-lg text-slate-300'
                }`}
              >
                {para}
              </p>
            ))}

            {/* End decoration */}
            <div className="flex items-center gap-3 mt-8 pt-6 border-t border-slate-700/50">
              <div className="flex-1 h-px bg-gradient-to-r from-emerald-500/40 to-transparent" />
              <span className="text-emerald-500/60 text-lg">🐦</span>
              <div className="flex-1 h-px bg-gradient-to-l from-emerald-500/40 to-transparent" />
            </div>
          </div>

          {/* ── Comments ──────────────────────────────────────────── */}
          <div
            ref={commentsRef}
            className="bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 shadow-xl"
          >
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              Discussion
              <span className="text-sm font-normal text-slate-500 bg-slate-700/60 px-2.5 py-0.5 rounded-full border border-slate-600/50">
                {comments.length}
              </span>
            </h2>

            {/* Comment list */}
            {comments.length > 0 ? (
              <div className="space-y-3 mb-8">
                {comments.map(comment => (
                  <div
                    key={comment.id}
                    data-comment-id={comment.id}
                    className="flex gap-3 group"
                  >
                    {comment.userImage ? (
                      <img src={comment.userImage} alt="" className="w-9 h-9 rounded-full flex-shrink-0 mt-0.5 ring-1 ring-slate-600" />
                    ) : (
                      <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 mt-0.5">
                        {comment.author?.charAt(0) || 'U'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="bg-slate-700/50 border border-slate-600/40 rounded-2xl rounded-tl-sm px-4 py-3 group-hover:border-slate-500/50 transition-colors">
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-sm font-semibold text-slate-200 truncate">
                            {comment.author || 'Anonymous'}
                          </span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-slate-500">
                              {format(new Date(comment.date), 'MMM dd · h:mm a')}
                            </span>
                            {comment.userId === userId && (
                              <button
                                onClick={() => handleDeleteComment(comment.id)}
                                className="text-slate-600 hover:text-red-400 transition opacity-0 group-hover:opacity-100"
                                title="Delete comment"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-slate-300 text-sm leading-relaxed">{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10 mb-8">
                <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-slate-700/50 border border-slate-600/40 flex items-center justify-center text-2xl">
                  💬
                </div>
                <p className="text-slate-400 text-sm">No comments yet. Be the first to share your thoughts!</p>
              </div>
            )}

            {/* Divider */}
            <div className="h-px bg-gradient-to-r from-transparent via-slate-600/50 to-transparent mb-6" />

            {/* Add comment form */}
            {isSignedIn ? (
              <div className="flex gap-3">
                {user?.imageUrl ? (
                  <img src={user.imageUrl} alt="" className="w-9 h-9 rounded-full flex-shrink-0 mt-0.5 ring-1 ring-emerald-500/30" />
                ) : (
                  <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 mt-0.5">
                    {user?.fullName?.charAt(0) || 'U'}
                  </div>
                )}
                <div className="flex-1">
                  <textarea
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAddComment() }}
                    placeholder="Share your thoughts… (Ctrl+Enter to post)"
                    rows={3}
                    className="w-full px-4 py-3 bg-slate-700/60 border border-slate-600/50 text-white placeholder-slate-500 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none text-sm transition"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-600">Ctrl+Enter to post quickly</span>
                    <button
                      onClick={handleAddComment}
                      disabled={!commentText.trim() || isSubmittingComment}
                      className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-5 py-2 rounded-xl font-semibold text-sm hover:from-emerald-500 hover:to-green-500 disabled:opacity-40 disabled:cursor-not-allowed transition shadow-lg shadow-emerald-500/20"
                    >
                      {isSubmittingComment ? (
                        <span className="flex items-center gap-1.5">
                          <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Posting…
                        </span>
                      ) : 'Post Comment'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-2xl p-6 text-center">
                <div className="text-3xl mb-2">💬</div>
                <p className="text-slate-300 font-medium mb-4">Sign in to join the discussion</p>
                <button
                  onClick={() => navigate('/sign-in')}
                  className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:from-emerald-500 hover:to-green-500 transition shadow-lg shadow-emerald-500/20"
                >
                  Sign In
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {editingPost && (
        <EditModal post={editingPost} onSave={handleSaveEdit} onClose={() => setEditingPost(null)} />
      )}
    </div>
  )
}

export default BlogDetail
